import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import clsx from 'clsx';
import i18n from 'i18n-js';

import { Button, Typography } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import RestaurantDetailsForm from '../CreateEditRestaurantPage/components/RestaurantsDetailsForm';
import { RestaurantCreateData } from '../CreateEditRestaurantPage';
import ObjectID from 'bson-objectid';
import agent from 'api/agent';
import { RestaurantDetails } from 'store/restaurantDetailsStore/reducer';
import { useStyles } from './styles';
import { BackArrow, FormSkeleton, PopupConfirmation } from 'components/shared';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import { FeaturesSelectorStep } from './components';
import { RestaurantFeatures } from './components/FeaturesSelectorStep';
import { RestaurantCopyModel } from 'api/controllers/restaurants';
import { useAppSelector } from 'index';
import { LocalStorageKeys } from 'constants/localStorageKeys';

export interface RestaurantAdminData {
    formData: RestaurantCreateData;
}

const defaultRestaurantDetails: RestaurantDetails = {
    id: '',
    name: '',
    addressLine: '',
    zip: '',
    city: '',
    phoneNumber: '',
    email: '',
    website: '',
    latitude: 0,
    longitude: 0,
    contactPersons: [],
    paymentDetails: {
        firmName: '',
        firstName: '',
        lastName: '',
        address: '',
        zip: '',
        city: '',
        accountNumber: '',
        iban: '',
        vat: '',
        tax: 0,
        currency: '',
        thanksMessage: '',
    },
    timeZone: '',
    qrCodesData: { dineInQrCodeName: '', takeAwayQrCodeName: '', homeDeliveryQrName: '' },
    dineIn: {
        isActive: false,
        workingHours: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
        },
        isDeliverAnytime: false,
        isPayToClose: false,
    },
    takeAway: {
        isActive: false,
        workingHours: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
        },
        isDeliverAnytime: false,
        isPayToClose: false,
    },
    homeDelivery: {
        isActive: false,
        workingHours: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
        },
        isDeliverAnytime: false,
        isPayToClose: false,
    },
    pricingLevels: [],
    coolBoxData: {
        isCoolBox: false,
        coolBoxName: '',
        isPublic: false,
    },
    type: [],
    isDeliverAnytime: false,
};

export const defaultFormValues: RestaurantCreateData = {
    name: '',
    addressLine: '',
    zip: '',
    city: '',
    phoneNumber: '',
    email: '',
    website: '',
    logoUrl: '',
    latitude: 0,
    longitude: 0,
    percentageForDineIn: 1,
    percentageForTakeAway: 1,
    percentageForHomeDelivery: 1,
    type: [],
    timeZone: '',
    isCoolBox: false,
    isPublic: false,
    coolBoxName: '',
    isDeliverAnytime: false,
};

export default function CopyRestaurantPage() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams<{ id: string }>();

    const cuisineTypes = useAppSelector((state) => state.restaurantDetails.cuisineTypes);

    const [restaurantDetails, setRestaurantDetails] = useState<RestaurantDetails>({
        ...defaultRestaurantDetails,
    });
    const [submitFn, setSubmitFnState] = useState<() => void>(() => () => {});
    const [formStatus, setFormStatusState] = useState({
        dirty: false,
        isValid: false,
        isSubmitting: false,
    });
    const [tab, setTab] = useState(0);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    const copiedRestaurantId = useRef('');

    const setSubmitFn = useCallback((fn: () => void) => {
        setSubmitFnState(() => fn);
    }, []);

    const setFormStatus = useCallback((status: typeof formStatus) => {
        setFormStatusState((prev) => {
            if (
                prev.dirty !== status.dirty ||
                prev.isValid !== status.isValid ||
                prev.isSubmitting !== status.isSubmitting
            ) {
                return status;
            }
            return prev;
        });
    }, []);

    const handleSubmit = () => {
        submitFn();
        setTab(1);
    };

    function saveRestaurantDetails(data: RestaurantCreateData) {
        const newRestaurantDetails: RestaurantDetails = {
            ...data,
            coolBoxData: {
                coolBoxName: data.coolBoxName || '',
                isCoolBox: data.isCoolBox,
                isPublic: data.isPublic,
            },
            paymentDetails: restaurantDetails.paymentDetails,
            qrCodesData: restaurantDetails.qrCodesData,
            contactPersons: [...restaurantDetails.contactPersons],
            openHours: restaurantDetails.openHours,
            id: restaurantDetails.id,
            homeDelivery: { ...restaurantDetails.homeDelivery },
            takeAway: { ...restaurantDetails.takeAway },
            dineIn: {
                ...restaurantDetails.dineIn,
            },
            pricingLevels: [],
            type: cuisineTypes.filter((t) => data.type.includes(t.id)),
        };
        localStorage.setItem(
            LocalStorageKeys.restaurantToCopy,
            JSON.stringify(newRestaurantDetails)
        );
        setRestaurantDetails(newRestaurantDetails);
    }

    function submitRestaurantCopy(data: RestaurantFeatures) {
        const storedData = localStorage.getItem(LocalStorageKeys.restaurantToCopy);
        if (storedData) {
            const restoredData = JSON.parse(storedData) as RestaurantDetails;
            const model: RestaurantCopyModel = {
                ...data,
                restaurantModel: {
                    ...restoredData,
                    logoUrl: restoredData.logoUrl || '',
                    isCoolBox: restoredData.coolBoxData.isCoolBox,
                    isPublic: restoredData.coolBoxData.isPublic,
                    coolBoxName: restoredData.coolBoxData.coolBoxName,
                    isDeliverAnytime: restoredData.isDeliverAnytime,
                    reservationModule: { isEnabled: false },
                    percentageForDineIn: restoredData.percentageForDineIn || 0,
                    percentageForHomeDelivery: restoredData.percentageForHomeDelivery || 0,
                    percentageForTakeAway: restoredData.percentageForTakeAway || 0,
                    type: restoredData.type.map((t) => t.id),
                },
            };
            dispatch(globalStore.actionCreators.showSpiner());
            agent.Restaurants.CopyRestaurant(restaurantDetails.id, model)
                .then((res) => {
                    copiedRestaurantId.current = res.id;
                    setShowSuccessPopup(true);
                })
                .catch((ex) => dispatch(globalStore.actionCreators.showToaster('error', ex)))
                .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
        }
    }

    function getRestaurantDetails() {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Restaurants.GetDetails(params.id)
            .then((response) => {
                setRestaurantDetails(response);
            })
            .catch((error) => {
                dispatch(globalStore.actionCreators.showToaster('error', error));
                history.goBack();
            })
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }

    function onSuccessButton() {
        history.push(`/restaurants/${copiedRestaurantId.current}`);
        localStorage.removeItem(LocalStorageKeys.restaurantToCopy);
    }

    useEffect(() => {
        const storedData = localStorage.getItem(LocalStorageKeys.restaurantToCopy);
        if (ObjectID.isValid(params.id)) {
            if (storedData) {
                const data = JSON.parse(storedData) as RestaurantDetails;
                if (data.id === params.id) {
                    setRestaurantDetails(JSON.parse(storedData));
                } else {
                    getRestaurantDetails();
                    localStorage.removeItem(LocalStorageKeys.restaurantToCopy);
                }
            } else {
                getRestaurantDetails();
            }
        } else {
            history.goBack();
        }
    }, [params.id, tab]);
    return (
        <>
            <div className={classes.headerContainer}>
                <div>
                    <BackArrow onClick={tab === 0 ? undefined : () => setTab(0)} />
                    <Typography
                        display="inline"
                        className={clsx(classes.wizzardStepTitle, {
                            [classes.wizzardStepTitleDisabled]: tab !== 0,
                        })}
                    >
                        {i18n.t('navigationTabs.copyWizzardFirstStep')}
                    </Typography>
                    <Typography
                        display="inline"
                        className={clsx(classes.wizzardStepTitle, {
                            [classes.wizzardStepTitleDisabled]: tab !== 0,
                        })}
                    >
                        <ArrowForwardIosIcon
                            style={{ position: 'relative', top: 2, fontSize: 15 }}
                        />
                    </Typography>
                    <Typography
                        className={clsx(classes.wizzardStepTitle, {
                            [classes.wizzardStepTitleDisabled]: tab !== 1,
                        })}
                        display="inline"
                        color="textSecondary"
                    >
                        {i18n.t('navigationTabs.copyWizzardSecondStep')}
                    </Typography>
                </div>
                <div>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => history.goBack()}
                        style={{ marginRight: 15 }}
                    >
                        {i18n.t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!formStatus.isValid || formStatus.isSubmitting}
                    >
                        {tab === 0 ? i18n.t('common.next') : 'Create restaurant'}
                    </Button>
                </div>
            </div>
            {tab === 0 ? (
                <RestaurantDetailsForm
                    restaurant={restaurantDetails}
                    onMountSubmit={setSubmitFn}
                    onStatusChange={setFormStatus}
                    onSubmit={saveRestaurantDetails}
                />
            ) : (
                <FeaturesSelectorStep onMountSubmit={setSubmitFn} onSubmit={submitRestaurantCopy} />
            )}
            <PopupConfirmation
                open={showSuccessPopup}
                close={() => setShowSuccessPopup(false)}
                title={i18n.t('restaurant.restaurantSuccefsullyCopied')}
                description={i18n.t('restaurant.ownerWillReceivetEmail')}
                activeBtn="Okay"
                action={onSuccessButton}
                hideCancel
            />
            <FormSkeleton
                isLoading={!restaurantDetails.id}
                columns={[7]}
                aside={3}
                style={{ top: 56 }}
            />
        </>
    );
}
